import { Alert, Button, Card, Form, Input, Modal, Space, Typography } from 'antd'
import { useState } from 'react'
import { useReprocessRainforestEventMutation } from './rainforestEventsQueries'
import { getReprocessErrorMessage, parseRainforestEventPayload } from './rainforestEventsModel'

const { Paragraph, Text } = Typography
const { TextArea } = Input

const RainforestEventsSection = () => {
  const [eventId, setEventId] = useState('')
  const [payload, setPayload] = useState('')
  const mutation = useReprocessRainforestEventMutation()

  const parsed = parseRainforestEventPayload(payload)
  const showInvalidWarning = payload.trim() !== '' && !parsed.isValid

  const handlePayloadChange = (value: string) => {
    setPayload(value)
    if (mutation.isSuccess || mutation.isError) {
      mutation.reset()
    }
  }

  const handleReprocess = () => {
    if (!parsed.isValid) {
      return
    }
    Modal.confirm({
      title: 'Reprocess this Rainforest event?',
      okText: 'Reprocess',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      width: 540,
      content: (
        <div>
          <Paragraph style={{ marginBottom: 8 }}>
            Detected event type: <Text strong>{parsed.eventType ?? 'unknown'}</Text>
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            Reprocessing re-runs this event through the normal webhook pipeline. Deposit events are
            idempotent, but other event types may re-fire side effects (e.g. receipt emails).
          </Paragraph>
        </div>
      ),
      onOk: () => {
        mutation.mutate(payload.trim())
      },
    })
  }

  return (
    <Card title="Reprocess Rainforest Event" style={{ maxWidth: 760 }}>
      <Paragraph type="secondary">
        Paste the raw event JSON copied from the Rainforest dashboard to re-run it through the
        webhook processing pipeline. Use this to recover events that were never processed.
      </Paragraph>
      <Form layout="vertical">
        <Form.Item label="Event ID" extra="For your reference and audit only — not sent to the server.">
          <Input
            placeholder="evt_..."
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
          />
        </Form.Item>
        <Form.Item
          label="Event payload (JSON)"
          required
          validateStatus={showInvalidWarning ? 'error' : undefined}
          help={showInvalidWarning ? 'Payload must be valid JSON for a single event object.' : undefined}
        >
          <TextArea
            rows={12}
            placeholder='{ "event_type": "deposit.succeeded", "data": { ... } }'
            value={payload}
            onChange={(e) => handlePayloadChange(e.target.value)}
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>
        <Button
          type="primary"
          onClick={handleReprocess}
          loading={mutation.isLoading}
          disabled={!parsed.isValid}
        >
          Reprocess
        </Button>
      </Form>

      {(mutation.isSuccess || mutation.isError) && (
        <div style={{ marginTop: 24 }}>
          {mutation.isSuccess && (
            <Alert
              type="success"
              showIcon
              message="Event reprocessed"
              description={
                <Space direction="vertical" size={4}>
                  <span>
                    Detected event type: <Text strong>{mutation.data?.eventType || 'unknown'}</Text>
                  </span>
                  {eventId.trim() !== '' && (
                    <span>
                      Event ID: <Text code>{eventId.trim()}</Text>
                    </span>
                  )}
                </Space>
              }
            />
          )}
          {mutation.isError && (
            <Alert
              type="error"
              showIcon
              message="Reprocessing failed"
              description={getReprocessErrorMessage(mutation.error)}
            />
          )}
        </div>
      )}
    </Card>
  )
}

export default RainforestEventsSection
